import React, {useCallback, useMemo, useRef} from 'react'
import {usePinch} from '@use-gesture/react'
import throttle from 'lodash/throttle'
import {MotionValue, PanInfo, Transition} from 'motion'
import {useAnimation, useDragControls} from 'motion/react'
import {once} from 'ramda'

import {PaneStyled, Pinchable} from './styled'

interface Props {
  index: number
  paneIndex: number
  isZoomEnabled: boolean
  isDragEnabled: boolean
  renderPane: (paneIndex: number) => React.ReactNode
  x: MotionValue
  onDragEnd(event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo): void
  onZoom?: () => void
  onImageClick?: () => void
}

export type RenderPane = Props['renderPane']

const pinchConfig: Transition = {
  type: 'spring',
  mass: 0.5,
  stiffness: 220,
  damping: 14
}

type Point = [number, number]
const diff = (point1: Point, point2: Point): Point => [
  point1[0] - point2[0],
  point1[1] - point2[1]
]

export const Pane = ({
  index,
  paneIndex,
  isZoomEnabled,
  isDragEnabled,
  renderPane,
  x,
  onDragEnd,
  onZoom,
  onImageClick
}: Props) => {
  const child = React.useMemo(
    () => renderPane(paneIndex),
    [paneIndex, renderPane]
  )

  const controls = useDragControls()
  const allowDragRef = useRef<boolean>(true)
  const pinchRef = useRef<HTMLDivElement>(null)
  const animation = useAnimation()
  const isDraggingRef = useRef<boolean>(false)

  // Throttling the pinch handler causes it to lose it's `memo` state so we throttle the animation
  const animateStartThrottled = useMemo(
    () => throttle(animation.start, 50, {leading: false, trailing: true}),
    [animation]
  )

  const {current: trackZoomedOnce} = useRef(
    once(() => {
      if (onZoom) onZoom()
    })
  )

  usePinch(
    state => {
      if (!pinchRef.current) return

      const {pinching, origin, offset, first, last, memo} = state

      // Prevent the drag, enable is delayed to prevent jank (when one finger is always released earlier)
      if (first) {
        allowDragRef.current = false
      }

      if (last) {
        setTimeout(() => {
          allowDragRef.current = true
        }, 300)
      }

      const [scale] = offset

      const startingOrigin = first ? origin : memo
      const originOffset = diff(startingOrigin, origin)

      const x =
        (pinchRef.current.clientWidth / 2 - origin[0] - originOffset[0]) * scale
      const y =
        (pinchRef.current.clientHeight / 2 - origin[1] - originOffset[1]) *
        scale

      if (isZoomEnabled) trackZoomedOnce()

      animateStartThrottled(
        pinching
          ? {
              scale,
              x,
              y
            }
          : {
              scale: 1,
              x: 0,
              y: 0
            },
        pinchConfig
      )

      // Store origin in memo
      return startingOrigin
    },
    {
      target: pinchRef.current || undefined,
      from: [1, 0],
      enabled: isZoomEnabled
    }
  )

  const onDragStart = useCallback(() => {
    isDraggingRef.current = true
    if (!allowDragRef.current) {
      controls.stop()
    }
  }, [controls])

  const handleDragEnd = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      isDraggingRef.current = false
      onDragEnd(event, info)
    },
    [onDragEnd]
  )

  const handleImageClick = useCallback(() => {
    // Only trigger click if we're not dragging
    if (!isDraggingRef.current && onImageClick) {
      onImageClick()
    }
  }, [onImageClick])

  return (
    <PaneStyled
      draggable={isDragEnabled}
      drag={isDragEnabled ? 'x' : false}
      dragControls={controls}
      dragElastic={1}
      style={{
        x,
        left: `${index * 100}%`
      }}
      onDragStart={onDragStart}
      onDragEnd={handleDragEnd}
    >
      <Pinchable
        ref={pinchRef}
        animate={animation}
        onClick={handleImageClick}
        style={onImageClick ? {cursor: 'pointer'} : undefined}
      >
        {child}
      </Pinchable>
    </PaneStyled>
  )
}
